import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { useTranslation } from '@hooks/useTranslation';
import { useMyDailySiteReports, useDailySiteReportSettings } from '@hooks/api/dailySiteReport';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { formatNextSend } from '@utils/dailySiteReport/formatNextSend';
import { ReportListItem } from '@components/organisms/DailySiteReport/ReportListItem';

const PAGE_SIZE = 30;

const DailySiteReportsPage: FC = () => {
  const t = useTranslation('dailySiteReport');
  const tCommon = useTranslation('common');
  const history = useHistory();
  const activeLocale = useAppSelector((state: RootState) => state.locale.activeLocale);
  const [page, setPage] = useState(0);

  const { data: settings } = useDailySiteReportSettings();
  const { data: reports, isLoading } = useMyDailySiteReports(PAGE_SIZE, page * PAGE_SIZE);

  // Story-204: the real next send, in the org timezone; a time-free sentence when it is unknown.
  const nextSend = formatNextSend(
    settings?.nextScheduledSendAt,
    settings?.organizationTimezone,
    new Date(),
    activeLocale === 'es' ? 'es' : 'en',
    t,
  );
  const emptyBody = settings?.organizationTimezone
    ? nextSend
      ? t.emptyBodyScheduled.replace('{when}', nextSend)
      : t.emptyBodyScheduledFallback
    : t.emptyBodyNoTimezone;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <SummarizeOutlinedIcon sx={{ fontSize: 28 }} className="text-orange-400" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.listTitle}</h1>
      </div>

      {isLoading && <Loading center size="lg" />}

      {!isLoading && (!reports || reports.length === 0) && (
        <div className="text-center py-16">
          <p className="text-lg font-medium text-gray-900 dark:text-white">{t.emptyTitleGeneric}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{emptyBody}</p>
        </div>
      )}

      {!isLoading && reports && reports.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          {reports.map((report) => (
            <ReportListItem
              key={report.reportId}
              report={report}
              onPress={() => history.push(`/reports/daily/${report.reportId}`)}
            />
          ))}
        </div>
      )}

      <div className="flex justify-between mt-4">
        <Button type="button" variant="secondary" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
          {tCommon.previous}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!reports || reports.length < PAGE_SIZE}
          onClick={() => setPage((p) => p + 1)}>
          {tCommon.next}
        </Button>
      </div>
    </div>
  );
};

export default DailySiteReportsPage;
